import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { ordersAPI } from '../services/api';
import { toast } from 'react-toastify';

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await ordersAPI.getById(id);
      setOrder(response.data);
    } catch (error) {
      toast.error('Failed to load order details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (!order) {
    return (
      <Container className="my-5">
        <Alert variant="danger">Order not found</Alert>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h1 className="mb-4">Order Details</h1>

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5>Order Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Order ID:</strong> {order._id}</p>
                  <p><strong>Order Date:</strong> {new Date(order.createdAt).toLocaleString()}</p>
                  <p><strong>Payment Status:</strong> <Badge bg={order.paymentStatus === 'completed' ? 'success' : 'warning'}>{order.paymentStatus}</Badge></p>
                </Col>
                <Col md={6}>
                  <p><strong>Payment ID:</strong> {order.razorpayPaymentId || 'N/A'}</p>
                  <p><strong>Total Amount:</strong> ₹{order.totalAmount}</p>
                  <p><strong>Delivery Status:</strong> <Badge bg={order.deliveryStatus === 'sent' ? 'success' : 'warning'}>{order.deliveryStatus}</Badge></p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <h5>Order Items & License Keys</h5>
            </Card.Header>
            <Card.Body>
              {order.orderItems.map((item, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <h6>{item.name}</h6>
                    <p className="mb-2">
                      <strong>Quantity:</strong> {item.quantity} | 
                      <strong className="ms-2">Price:</strong> ₹{item.pricePerUnit} each | 
                      <strong className="ms-2">Total:</strong> ₹{item.pricePerUnit * item.quantity}
                    </p>
                    
                    {item.licenseKeys && item.licenseKeys.length > 0 && (
                      <div className="mt-3">
                        <Alert variant="success">
                          <strong>Your License Key(s):</strong>
                          {item.licenseKeys.map((key, i) => (
                            <div key={i} className="mt-2">
                              <code className="d-block bg-white p-2 rounded text-dark">
                                {key}
                              </code>
                            </div>
                          ))}
                        </Alert>
                        
                        {item.productId?.downloadUrl && (
                          <Alert variant="info">
                            <strong>Download Link:</strong><br />
                            <a href={item.productId.downloadUrl} target="_blank" rel="noopener noreferrer">
                              {item.productId.downloadUrl}
                            </a>
                          </Alert>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5>Customer Information</h5>
            </Card.Header>
            <Card.Body>
              <p><strong>Name:</strong> {order.customerInfo.name}</p>
              <p><strong>Email:</strong> {order.customerInfo.email}</p>
              <p><strong>Phone:</strong> {order.customerInfo.phone || 'N/A'}</p>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h5>Delivery Status</h5>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Email Sent:</strong> {order.emailSent ? 
                  <Badge bg="success">Yes</Badge> : 
                  <Badge bg="secondary">No</Badge>
                }
              </p>
              <p>
                <strong>WhatsApp Sent:</strong> {order.whatsappSent ? 
                  <Badge bg="success">Yes</Badge> : 
                  <Badge bg="secondary">No</Badge>
                }
              </p>
            </Card.Body>
          </Card>

          {order.paymentStatus === 'completed' && (
            <Alert variant="info" className="mt-3">
              <small>
                <strong>Note:</strong> Your license keys have been sent to your email and WhatsApp. 
                Please check your inbox and spam folder.
              </small>
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default OrderDetail;
